import React, { useEffect, useRef, useState } from "react";
import {
  Form,
  Input,
  Spin,
  Select,
  Divider,
  InputNumber,
  Space,
  Tag,
  DatePicker,
} from "antd";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import { save } from "../../../features/save/saveSlice";
import BreadCrumb from "../../../layout/BreadCrumb";
import {
  fetchData,
  fetchLocation,
  fetchSubCounties,
  fetchSubLocation,
  fetchSubsidySource,
} from "../../../features/fetch/fetchSlice";
import { FldControlStatusData } from "../../../data";
import dayjs from "dayjs";
import { normalizeDateToLocalYear } from "../../../utils";

const { TextArea } = Input;
const FloodControlAdd = () => {
  const urlWtrSrce = "water_sources/fetch_watersourcess"; 
  const urlCategory = "flood_category/fetch_floodcategorys";
  const urlPathway = "api/v2/irrigationPathway/get";
  const urlAgency = "api/v2/agency/get";

  const urlWard = "ward/fetch_wards";
  const urlConstituency = "constituency/fetch_constituencys";

  const [form] = Form.useForm();
  const formRef = useRef(null);
  const { saving } = useSelector((state) => state.save);
  const { counties, subCounties, locations, subLocations,ward,constituency, subsidySources } =
    useSelector((state) => state.fetch);
  const {
    activeObjFloodContrl, 
    dataWtrSrc,
    dataAuxiliary,
    dataCatType,
    dataPathway,
    dataAgency,
    dataConstituency,
    dataWard,
  } = useSelector((state) => ({
    activeObjFloodContrl: state.obj.activeObj["floodControl"],
    dataWtrSrc: state.fetch.data[urlWtrSrce] || [], 
    dataCatType: state.fetch.data[urlCategory] || [],
    dataPathway: state.fetch.data[urlPathway] || [],
    dataAgency: state.fetch.data[urlAgency] || [],
    dataWard: state.fetch.data[urlWard] || [],
    dataConstituency: state.fetch.data[urlConstituency] || [],
  }));

  const dispatch = useDispatch();
  const navigate = useNavigate();

  function handleCancel() {
    navigate("/rob/flood-control");
  }

  const [countyName, setCountyName] = useState(
    activeObjFloodContrl?.countyName ?? ""
  );

  const onFinish = async (data) => {
    data.floodId = activeObjFloodContrl?.floodId
      ? activeObjFloodContrl?.floodId
      : null;
    data.floodAuxiliariesId = Array.isArray(data?.floodAuxiliariesId)
      ? data?.floodAuxiliariesId.join(",")
      : data?.floodAuxiliariesId;

    data.floodPathwayId = Array.isArray(data?.floodPathwayId)
      ? data?.floodPathwayId.join(",")
      : data?.floodPathwayId;

    data.floodSourceFundsId = Array.isArray(data?.floodSourceFundsId)
      ? data?.floodSourceFundsId.join(",")
      : data?.floodSourceFundsId;
    data.floodStartDate = normalizeDateToLocalYear(data?.floodStartDate);
    data.floodEndDate = normalizeDateToLocalYear(data?.floodEndDate);
    const res = await dispatch(
      save({
        ...data,
        url: "flood_control/save_floodcontrol",
      })
    );
    if (res?.payload?.success) {
      await toast.success(res?.payload?.message);
      await form.resetFields();
      await handleCancel();
    } else {
      toast.error(res?.payload?.message);
    }
  };

  const [floodCountyId, setFloodCountyId] = useState(activeObjFloodContrl?.floodCountyId ?? "");
  const [floodConstId, setFloodConstId] = useState(activeObjFloodContrl?.floodConstId ?? "");
  const [floodSubCountyId, setFloodSubCountyId] = useState(activeObjFloodContrl?.floodSubCountyId ?? "");
  const [floodLocationId, setFloodLocationId] = useState(activeObjFloodContrl?.floodLocationId ?? "");
  const [floodSubLocationId, setFloodSubLocationId] = useState(activeObjFloodContrl?.floodSubLocationId ?? "");
  const [floodWardId, setFloodWardId] = useState(activeObjFloodContrl?.floodWardId ?? "");
  
  const handleFloodCountyChange = async (value) => {
    setFloodCountyId(value);
    setFloodConstId("");
    setFloodSubCountyId("");
    setFloodLocationId("");
    setFloodSubLocationId("");
    setFloodWardId("");
  
    form.setFieldsValue({
      floodCountyId: value,
      floodConstId: "",
      floodSubCountyId: "",
      floodLocationId: "",
      floodSubLocationId: "",
      floodWardId: "",
    });
    await dispatch(fetchSubCounties({ subCountyId: value }));
    await dispatch(
      fetchData({
        url: urlConstituency,
        constCountyId: value,
      })
    );
  };
  
  const handleFloodConstituencyChange = async (value) => {
    setFloodConstId(value);
    setFloodWardId("");
    form.setFieldsValue({ floodConstId: value, floodWardId: "" });
    await dispatch(
      fetchData({
        url: urlWard,
        wdConstId: value,
      })
    );
  };
  
  const handleFloodSubCountyChange = async (value) => {
    setFloodSubCountyId(value);
    setFloodLocationId("");
    setFloodSubLocationId("");
    form.setFieldsValue({
      floodSubCountyId: value,
      floodLocationId: "",
      floodSubLocationId: "",
    });
    await dispatch(fetchLocation({ locSubId: value }));
  };
  
  const handleFloodLocationChange = async (value) => {
    setFloodLocationId(value);
    setFloodSubLocationId("");
    form.setFieldsValue({ floodLocationId: value, floodSubLocationId: "" });
    await dispatch(fetchSubLocation({ slocLocId: value }));
  };
  
  const handleFloodSubLocationChange = (value) => {
    setFloodSubLocationId(value);
    form.setFieldsValue({ floodSubLocationId: value });
  };
  
  const handleFloodWardChange = (value) => {
    setFloodWardId(value);
    form.setFieldsValue({ floodWardId: value });
  };
  
  const handleDatesChange = (_, allValues) => {
    const { floodStartDate, floodEndDate } = allValues;
    if (floodStartDate && floodEndDate) {
      const diff = dayjs(floodEndDate)?.diff(dayjs(floodStartDate), "day");
      form.setFieldsValue({
        floodTimeFrame: `${diff} day${diff !== 1 ? "s" : ""}`,
      });
    } else {
      form.setFieldsValue({ floodTimeFrame: "" });
    }
  };

  useEffect(() => {
    dispatch(
      fetchData({
        url: urlWtrSrce,
        limit: null,
        start: 0,
      })
    ); 
    dispatch(
      fetchData({
        url: urlCategory,
        limit: null,
        start: 0,
      })
    );
    dispatch(
      fetchData({
        url: urlPathway,
        limit: null,
        start: 0,
      })
    );
    dispatch(
      fetchData({
        url: urlAgency,
        limit: null,
        start: 0,
      })
    );

    dispatch(fetchSubsidySource());
  }, []);

  const initialFormValues = {
    ...activeObjFloodContrl,
    floodStartDate: activeObjFloodContrl?.floodStartDate
      ? dayjs(new Date(activeObjFloodContrl?.floodStartDate))
      : null,
    floodEndDate: activeObjFloodContrl?.floodEndDate
      ? dayjs(new Date(activeObjFloodContrl?.floodEndDate))
      : null,
  };

  useEffect(() => {
    form.setFieldsValue({
      ...activeObjFloodContrl,
      floodStartDate: activeObjFloodContrl?.floodStartDate
        ? dayjs(new Date(activeObjFloodContrl?.floodStartDate))
        : null,
      floodEndDate: activeObjFloodContrl?.floodEndDate
        ? dayjs(new Date(activeObjFloodContrl?.floodEndDate))
        : null,
    });
    if (activeObjFloodContrl?.floodSourceFundsId) {
      form.setFieldsValue({
        floodSourceFundsId: activeObjFloodContrl?.floodSourceFundsId
          ?.split(",")
          ?.map((b) => b?.trim()),
      });
    }
    if (activeObjFloodContrl?.floodAuxiliariesId) {
      form.setFieldsValue({
        floodAuxiliariesId: activeObjFloodContrl?.floodAuxiliariesId
          ?.split(",")
          ?.map((b) => b?.trim()),
      });
    }
    if (activeObjFloodContrl?.floodPathwayId) {
      form.setFieldsValue({
        floodPathwayId: activeObjFloodContrl?.floodPathwayId
          ?.split(",")
          ?.map((b) => b?.trim()),
      });
    }
  }, [activeObjFloodContrl, form]);

  useEffect(() => {
    if (activeObjFloodContrl?.floodId) {
      dispatch(
        fetchSubCounties({
          subCountyId: activeObjFloodContrl?.floodCountyId,
        })
      );
      dispatch(
        fetchData({
          url: urlConstituency,
          constCountyId: activeObjFloodContrl?.floodCountyId,
        })
      );
  
      dispatch(
        fetchData({
          url: urlWard,
          wdConstId: activeObjFloodContrl?.floodConstId,
        })
      );
      dispatch(
        fetchLocation({
          locSubId: activeObjFloodContrl?.floodSubCountyId,
        })
      );
      dispatch(
        fetchSubLocation({
          slocLocId: activeObjFloodContrl?.floodLocationId,
        })
      );
    }
  }, [activeObjFloodContrl, form]);
 

  return (
    <div className="w-full ">
      <BreadCrumb
        breadcrumbs={[
          { title: "Flood Control", path: "/rob/flood-control" },
          { title: "Flood Control Form" },
        ]}
      />
      <div className="white_card mt-[2.06rem]">
        <div className="form-add-h mb-[1.25rem]">Flood Control Information</div>
        <Divider />
        <div className="w-full mt-[1.75rem]">
          <Form
            layout="vertical"
            ref={formRef}
            name="control-ref"
            onFinish={onFinish}
            className=""
            style={{
              maxWidth: "100%",
            }}
            form={form}
            initialValues={initialFormValues}
            onValuesChange={handleDatesChange}
          >
            <div className="grid lg:grid-cols-2 grid-cols-1 lg:gap-x-10">
              <Form.Item
                name="floodName"
                label={"Name of Flood"}
                rules={[
                  {
                    required: true,
                    message: "Please add name of flood",
                  },
                ]}
              >
                <Input className="input" />
              </Form.Item>
              
              <Form.Item
  name="floodCountyId"
  label="County"
  rules={[{ required: true, message: "Please select county" }]}
>
  <Select
    allowClear
    options={counties?.map((c) => ({
      value: c?.countyId,
      label: c?.countyName,
    }))}
    onChange={handleFloodCountyChange}
    showSearch
    filterOption={(input, option) =>
      option?.label?.toLowerCase()?.includes(input.toLowerCase())
    }
  />
</Form.Item>

{floodCountyId && (
  <>
    <Form.Item
      name="floodConstId"
      label="Constituency"
      rules={[
        { required: true, message: "Please select constituency" },
      ]}
    >
      <Select
        allowClear
        options={dataConstituency?.map((c) => ({
          value: c?.constId,
          label: c?.constName,
        }))}
        onChange={handleFloodConstituencyChange}
        showSearch
        filterOption={(input, option) =>
          option?.label?.toLowerCase()?.includes(input.toLowerCase())
        }
      />
    </Form.Item>

    <Form.Item
      name="floodSubCountyId"
      label="Subcounty"
      rules={[
        { required: true, message: "Please select subcounty" },
      ]}
    >
      <Select
        allowClear
        options={subCounties?.map((s) => ({
          value: s?.subId,
          label: s?.subCountyName,
        }))}
        onChange={handleFloodSubCountyChange}
        showSearch
        filterOption={(input, option) =>
          option?.label?.toLowerCase()?.includes(input.toLowerCase())
        }
      />
    </Form.Item>
  </>
)}

{floodSubCountyId && (
  <Form.Item
    name="floodLocationId"
    label="Location"
    rules={[
      { required: true, message: "Please select location" },
    ]}
  >
    <Select
      allowClear
      options={locations?.map((l) => ({
        value: l?.locId,
        label: l?.locLocation,
      }))}
      onChange={handleFloodLocationChange}
      showSearch
      filterOption={(input, option) =>
        option?.label?.toLowerCase()?.includes(input.toLowerCase())
      }
    />
  </Form.Item>
)}

{floodLocationId && (
  <Form.Item
    name="floodSubLocationId"
    label="Sublocation"
    rules={[
      { required: true, message: "Please select sublocation" },
    ]}
  >
    <Select
      allowClear
      options={subLocations?.map((sl) => ({
        value: sl?.slocId,
        label: sl?.slocLocation,
      }))}
      onChange={handleFloodSubLocationChange}
      showSearch
      filterOption={(input, option) =>
        option?.label?.toLowerCase()?.includes(input.toLowerCase())
      }
    />
  </Form.Item>
)}

{floodConstId && (
  <Form.Item
    name="floodWardId"
    label="Ward"
    rules={[{ required: true, message: "Please select ward" }]}
  >
    <Select
      allowClear
      options={dataWard?.map((w) => ({
        value: w?.wdId,
        label: w?.wdWard,
      }))}
      onChange={handleFloodWardChange}
      showSearch
      filterOption={(input, option) =>
        option?.label?.toLowerCase()?.includes(input.toLowerCase())
      }
    />
  </Form.Item>
)}

              <Form.Item
                name="floodLat"
                label={"GPS Latitude (Decimal degrees, WGS84)"}
                rules={[
                  {
                    required: true,
                    message: "Please add latitude",
                  },
                ]}
              >
                <InputNumber
                  parser={(value) => value?.replace(/\$\s?|(,*)/g, "")}
                  className="input flex"
                />
              </Form.Item>
              <Form.Item
                name="floodLong"
                label={"GPS Longitude (Decimal degrees, WGS84)"}
                rules={[
                  {
                    required: true,
                    message: "Please add longitude",
                  },
                ]}
              >
                <InputNumber
                  parser={(value) => value?.replace(/\$\s?|(,*)/g, "")}
                  className="input flex"
                />
              </Form.Item>

              <Form.Item
                name="floodCost"
                label={"Cost"}
                rules={[
                  {
                    required: true,
                    message: "Please add cost",
                  },
                ]}
              >
                <InputNumber
                  min={0}
                  formatter={(value) =>
                    `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")
                  }
                  parser={(value) => value?.replace(/\$\s?|(,*)/g, "")}
                  placeholder="Enter value"
                  className="input flex"
                />
              </Form.Item>
              <Form.Item
                name="floodWaterSourceId"
                className=""
                label={"Water Source"}
                rules={[
                  {
                    required: true,
                    message: "Please select water source",
                  },
                ]}
              >
                <Select
                  className=""
                  allowClear
                  style={{
                    width: "100%",
                  }}
                  options={
                    dataWtrSrc?.length > 0 &&
                    dataWtrSrc?.map((item) => ({
                      value: item?.wtrId,
                      label: item?.wtrSource,
                    }))
                  }
                  showSearch
                  optionFilterProp="children"
                  filterOption={(input, option) =>
                    (option?.label?.toLocaleLowerCase() ?? "").includes(
                      input?.toLocaleLowerCase()
                    )
                  }
                />
              </Form.Item>
              <Form.Item
                name="floodControlCategoryId"
                className=""
                label={"Flood Category"}
                rules={[
                  {
                    required: true,
                    message: "Please select flood category type",
                  },
                ]}
              >
                <Select
                  className=""
                  allowClear
                  style={{
                    width: "100%",
                  }}
                  options={
                    dataCatType?.length > 0 &&
                    dataCatType?.map((item) => ({
                      value: item?.flId,
                      label: item?.flCategory,
                    }))
                  }
                  showSearch
                  optionFilterProp="children"
                  filterOption={(input, option) =>
                    (option?.label?.toLocaleLowerCase() ?? "").includes(
                      input?.toLocaleLowerCase()
                    )
                  }
                />
              </Form.Item>
              <Form.Item
                name="floodDykeLength"
                label={"Length"}
                rules={[
                  {
                    required: true,
                    message: "Please add length",
                  },
                ]}
              >
                <InputNumber
                  suffix={<span>M</span>}
                  parser={(value) => value?.replace(/\$\s?|(,*)/g, "")}
                  className="input flex"
                />
              </Form.Item>
              <Form.Item
                name="floodDykeHeight"
                label={"Height"}
                rules={[
                  {
                    required: true,
                    message: "Please add dyke height",
                  },
                ]}
              >
                <InputNumber
                  suffix={<span>M</span>}
                  parser={(value) => value?.replace(/\$\s?|(,*)/g, "")}
                  className="input flex"
                />
              </Form.Item>
              <Form.Item
                label={"Auxiliaries"}
                name="floodAuxiliariesId"
                rules={[
                  {
                    required: true,
                    message: "Auxiliaries is required",
                  },
                ]}
              >
                <Input className="input" />
              </Form.Item>
          
              <Form.Item
                name="floodAcreageProtected"
                label={"Acreage Protected"}
                rules={[
                  {
                    required: true,
                    message: "Please add acreage protected",
                  },
                ]}
              >
                <InputNumber
                  suffix="Acres"
                  parser={(value) => value?.replace(/\$\s?|(,*)/g, "")}
                  className="input flex"
                />
              </Form.Item>
              <Form.Item
                name="floodAcreageFarmed"
                label={"Acreage Farmed"}
                rules={[
                  {
                    required: true,
                    message: "Please add acreage farmed",
                  },
                ]}
              >
                <InputNumber
                  suffix="Acres"
                  parser={(value) => value?.replace(/\$\s?|(,*)/g, "")}
                  className="input flex"
                />
              </Form.Item>
              <Form.Item
                name="floodEstBeneficiaries"
                label={"Estimated Benaficiaries"}
                rules={[
                  {
                    required: true,
                    message: "Please add benaficiaries",
                  },
                ]}
              >
                <InputNumber
                  min={0}
                  formatter={(value) =>
                    `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")
                  }
                  parser={(value) => value?.replace(/\$\s?|(,*)/g, "")}
                  placeholder="Enter value"
                  className="input flex"
                />
              </Form.Item>

              <Form.Item
                name="floodStatus"
                className=""
                label={"Flood Control Status"}
                rules={[
                  {
                    required: true,
                    message: "Please select flood control status",
                  },
                ]}
              >
                <Select
                  className=""
                  allowClear
                  style={{
                    width: "100%",
                  }}
                  options={
                    FldControlStatusData?.length > 0 &&
                    FldControlStatusData?.map((item) => ({
                      value: item?.value,
                      label: item?.label,
                    }))
                  }
                  showSearch
                  optionFilterProp="children"
                  filterOption={(input, option) =>
                    (option?.label?.toLocaleLowerCase() ?? "").includes(
                      input?.toLocaleLowerCase()
                    )
                  }
                />
              </Form.Item>
              <Form.Item
                label="Start Date"
                name="floodStartDate"
                rules={[
                  {
                    required: true,
                    message: "Start date is required",
                  },
                ]}
              >
                <DatePicker format={"YYYY MMM DD"} className="input" />
              </Form.Item>
              <Form.Item
                label="End Date"
                name="floodEndDate"
                rules={[
                  {
                    required: true,
                    message: "End date is required",
                  },
                ]}
              >
                <DatePicker format={"YYYY MMM DD"} className="input" />
              </Form.Item>
              <Form.Item
                label="Time Frame"
                name="floodTimeFrame"
                rules={[
                  {
                    required: true,
                    message: "Time Frame date is required",
                  },
                ]}
              >
                <Input disabled className="input" />
              </Form.Item>
              <Form.Item
                label="Source of Funding"
                name="floodSourceFundsId"
                rules={[
                  { required: true, message: "Source of Funding is required" },
                ]}
              >
                <Select
                  mode="multiple"
                  allowClear
                  showSearch
                  dropdownRender={(menu) => (
                    <>
                      <Space style={{ padding: "8px 8px 4px" }}>
                        <span className="select-info mt-1">
                          Choose one or more of the following sources
                        </span>
                      </Space>
                      <Divider style={{ margin: "8px 0" }} />
                      {menu}
                    </>
                  )}
                  optionFilterProp="children"
                  filterOption={(input, option) =>
                    (option?.children?.toLowerCase() ?? "").includes(
                      input.toLowerCase()
                    )
                  }
                  tagRender={(props) => {
                    const { label, closable, onClose } = props;
                    return (
                      <span
                        style={{
                          display: "inline-flex",
                          alignItems: "center",
                          padding: "4px 8px",
                          margin: "2px",
                          borderRadius: "4px",
                          background: "#f5f5f5",
                        }}
                      >
                        {label}
                        {closable && (
                          <span
                            onClick={onClose}
                            style={{
                              marginLeft: "8px",
                              cursor: "pointer",
                              color: "red",
                            }}
                          >
                            ×
                          </span>
                        )}
                      </span>
                    );
                  }}
                  dropdownStyle={{ maxHeight: 400, overflowY: "auto" }}
                >
                  {subsidySources?.map((item) => (
                    <Select.Option
                      key={item?.subsidySourceId}
                      value={item?.subsidySourceId}
                    >
                      {item?.subsidySourceName}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>

              <Form.Item
                label={"Pathways"}
                name="floodPathwayId"
                rules={[
                  {
                    required: true,
                    message: "Please add pathways",
                  },
                ]}
              >
                <Select
                  mode="multiple"
                  allowClear
                  showSearch
                  dropdownRender={(menu) => (
                    <>
                      <Space style={{ padding: "8px 8px 4px" }}>
                        <span className="select-info mt-1">
                          Choose one or more of the following pathways
                        </span>
                      </Space>
                      <Divider style={{ margin: "8px 0" }} />
                      {menu}
                    </>
                  )}
                  optionFilterProp="children"
                  filterOption={(input, option) =>
                    (option?.children?.toLowerCase() ?? "").includes(
                      input.toLowerCase()
                    )
                  }
                  dropdownStyle={{ maxHeight: 400, overflowY: "auto" }}
                >
                  {dataPathway?.length > 0 &&
                    dataPathway?.map((item) => (
                      <Select.Option
                        key={item?.irrigationPathwayId}
                        value={item?.irrigationPathwayId}
                      >
                        {item?.irrigationPathwayName}
                      </Select.Option>
                    ))}
                </Select>
              </Form.Item>

              <Form.Item
                name="floodAgencyId"
                className=""
                label={"Implementing Entity/Department"}
                rules={[
                  {
                    required: true,
                    message: "Please select Implementing Entity/Department",
                  },
                ]}
              >
                <Select
                  className=""
                  allowClear
                  style={{
                    width: "100%",
                  }}
                  options={
                    dataAgency?.length > 0 &&
                    dataAgency?.map((item) => ({
                      value: item?.agencyId,
                      label: item?.agencyName,
                    }))
                  }
                  showSearch
                  optionFilterProp="children"
                  filterOption={(input, option) =>
                    (option?.label?.toLocaleLowerCase() ?? "").includes(
                      input?.toLocaleLowerCase()
                    )
                  }
                />
              </Form.Item>

              <Form.Item
                name="floodManagingAgency"
                label={"Managing Entity"}
                rules={[
                  {
                    required: true,
                    message: "Please add Managing Entity",
                  },
                ]}
              >
                <Input className="input" />
              </Form.Item>
            </div>
            <Form.Item
              name="floodRemarks"
              label={"Remarks"}
              rules={[
                {
                  required: true,
                  message: "Please add remarks",
                },
              ]}
            >
              <TextArea
                placeholder="Description..."
                rows={5}
                className="text_area"
              />
            </Form.Item>

            <Form.Item
              name="floodScope"
              label={"Scope"}
              rules={[
                {
                  required: true,
                  message: "Please add scope",
                },
              ]}
            >
              <TextArea
                placeholder="Description..."
                rows={5}
                className="text_area"
              />
            </Form.Item>
            <div className="flex justify-between mt-[48px] ">
              <div className="justify-start"></div>
              <div className="justify-end flex items-center mb-[58px] gap-x-5">
                <div className="w-[150px]">
                  <button
                    type="button"
                    key="back"
                    onClick={handleCancel}
                    className="cstm-btn"
                  >
                    Cancel
                  </button>
                </div>

                <div className="w-[150px]">
                  <button key="submit" type="submit" className="cstm-btn-2">
                    {saving ? (
                      <Spin />
                    ) : activeObjFloodContrl?.floodId ? (
                      "Update"
                    ) : (
                      "Create"
                    )}
                  </button>
                </div>
              </div>
            </div>
          </Form>
        </div>
      </div>
    </div>
  );
};
export default FloodControlAdd;
